import { BaseDocumentType as BaseDocumentTypeEnum } from "#graphql/supergraphEnums";
import { BaseDocumentType } from "#graphql/supergraphTypes";
import { DocumentSubType, DocumentSubTypeEnum } from "../DocumentCollector/types";
import { DocumentCategoryInfo } from "./types";

/**
 * Document categories. Based on the requirement documentation.
 */
const documentCategories: Partial<Record<BaseDocumentType, DocumentCategoryInfo>> = {
  [BaseDocumentTypeEnum.Id]: {
    title: "Identification document",
    description: "Government-issued identification containing a photograph and signature.",
    example: "Examples include: A passport or driver's license",
  },
  [BaseDocumentTypeEnum.AddressVerification]: {
    title: "Address Verification",
    description:
      "Utility bill, bank statement, government document, or equivalent dated within the 12 months.",
    example:
      "Examples include: Electricity bill, natural gas bill or a bank statement dated within the last 12 months",
  },
  [BaseDocumentTypeEnum.AmlRelianceLetter]: {
    title: "AML Reliance Letter",
    description:
      "Letter confirming reliance on another institution's anti-money laundering procedures.",
    example:
      "Examples include: An AML Reliance/Comfort Letter issued by a Fund Administrator/Custodian/Nominee",
  },
  [BaseDocumentTypeEnum.AnnualReport]: {
    title: "Annual Report",
    description:
      "Annual disclosure of a public company's financial and operational performance.",
    example: "Examples include: Annual Financial Report or Form 10-K",
  },
  [BaseDocumentTypeEnum.AuthorizedSignatoryList]: {
    title: "Authorized Signatory List",
    description:
      "Official list of individuals authorized to sign on behalf of the entity, including signature specimens.",
    example:
      "Examples include: Authorized Signatory List, Incumbency certificate, Signatory Authorization Form with signature specimens",
  },
  [BaseDocumentTypeEnum.ConfirmationOfClassBeneficiaries]: {
    title: "Confirmation of the class of beneficiaries",
    description:
      "Documentation or confirmation verifying the designated class of beneficiaries of a non-profit.",
    example: "Examples include: Mission Statement, Beneficiary Declaration, Trust Agreement",
  },
  [BaseDocumentTypeEnum.EvidenceOfFormation]: {
    title: "Evidence of Government Formation/Control over Entity",
    description:
      "Documentation demonstrating government oversight or approval of the entity's formation.",
    example:
      "Examples include: Government-issued Ownership Certificate, Regulatory filings showing government ownership, or screenshot of listing in a government-owned entity registry",
  },
  [BaseDocumentTypeEnum.EvidenceOfListingOnPublicExchange]: {
    title: "Evidence of listing on public exchange",
    description:
      "Evidence that entity is publicly traded and subject to requirements of a major exchange.",
    example: "Examples include: Screenshot of stock exchange listing, Bloomberg/Reuters report",
  },
  [BaseDocumentTypeEnum.EvidenceOfRegulation]: {
    title: "Evidence of Regulation",
    description:
      "Official documentation proving the entity is regulated by a governing authority.",
    example:
      "Examples include: Online regulatory registry search results, License verification from SEC, FCA, FINRA, or state regulators, Screenshot of online registration status",
  },
  [BaseDocumentTypeEnum.Form_990]: {
    title: "Form 990",
    description:
      "IRS form submitted by tax-exempt organizations to report financial information.",
    example: "Examples include: Form 990, Form 990-EZ",
  },
  [BaseDocumentTypeEnum.FormationDocument]: {
    title: "Formation Document",
    description:
      "Legal document issued by a government entity confirming the establishment and existence of an entity.",
    example: "Examples include: Certificate of Incorporation, Certificate of Formation",
  },
  [BaseDocumentTypeEnum.OrganizationChart]: {
    title: "Organization Chart",
    description:
      "Diagrammatic representation of an entity's governance and reporting structure.",
    example: "Examples include: Organizational chart depicting hierarchy and roles",
  },
  [BaseDocumentTypeEnum.OrganizationalDocument]: {
    title: "Organizational Document",
    description:
      "Formal document outlining the structure, governance, and key personnel of an entity.",
    example:
      "Examples include: Limited Partnership Agreement, Articles of Association, Operating Agreement",
  },
  [BaseDocumentTypeEnum.PayeeMismatchExplanation]: {
    title: "Payee Mismatch Explanation",
    description: "Written explanation addressing discrepancies in payee details",
    example: "",
  },
  [BaseDocumentTypeEnum.RegisterOfDirectors]: {
    title: "Register of directors",
    description: "List of company directors and their details.",
    example: "Examples include: Company's official director registry",
  },
  [BaseDocumentTypeEnum.RegisterOfMembers]: {
    title: "Register of members",
    description: "Register of members including ownership percentages.",
    example: "Examples include: Shareholder registry, Cap table",
  },
  [BaseDocumentTypeEnum.ShareRegister]: {
    title: "Share Register",
    description: "Record of issued shares and ownership details.",
    example: "Examples include: Stock ledger, Share issuance report",
  },
  [BaseDocumentTypeEnum.TrustDeed]: {
    title: "Trust Deed",
    description: "Trust Deed, Declaration of Trust, or equivalent.",
    example: "Examples include: Trust Agreement, Trust Deed",
  },
  [BaseDocumentTypeEnum.WSeriesTaxForm]: {
    title: "W series tax form",
    description: "Standard IRS tax form for reporting purposes.",
    example: "Examples include: W-8 or W-9 forms",
  },
};

/**
 * Document sub categories
 */
export const documentSubCategories: Record<DocumentSubType, DocumentCategoryInfo> = {
  [DocumentSubTypeEnum.ArticlesOfIncorporation]: {
    title: "Articles of Incorporation",
    description:
      "Formal document outlining the structure, governance, and key personnel of an entity.",
    example: "Example: Charter or Articles of Incorporation",
  },
  [DocumentSubTypeEnum.CertificateOfFormation]: {
    title: "Certificate of Formation",
    description:
      "Legal document issued by a government entity confirming the establishment and existence of an entity.",
    example: "Example: Certificate of Formation",
  },
  [DocumentSubTypeEnum.CertificateOfIncorporation]: {
    title: "Certificate of Incorporation",
    description:
      "Legal document issued by a government entity confirming the establishment and existence of an entity.",
    example: "Example: Certificate of Incorporation",
  },
  [DocumentSubTypeEnum.LimitedPartnership]: {
    title: "Limited Partnership/Operating Agreement",
    description:
      "Formal document outlining the structure, governance, and key personnel of an entity.",
    example: "Example includes: Limited Partnership, Operating Agreement",
  },
  [DocumentSubTypeEnum.CertificateOfFormOrPartnership]: {
    title: "Certificate of Formation/Partnership",
    description:
      "Legal document issued by a government entity confirming the establishment and existence of an entity.",
    example: "Example: Certificate of Formation/Partnership",
  },
};

/**
 * Get the document category info
 * @param baseType - The base type of the document
 * @param subType - The sub type of the document
 * @returns The document category info.
 * title -> if subType is provided, return the title from the {@link documentSubCategories} object
 * title -> if subType is not provided, return the title from the {@link documentCategories} object
 * description -> Based on the baseType, description is always taken from the {@link documentCategories} object
 */
export const getDocumentCategoryInfo = (
  baseType: BaseDocumentType,
  subType?: string | null,
): DocumentCategoryInfo => {
  if (subType && documentSubCategories[subType as DocumentSubType]) {
    return documentSubCategories[subType as DocumentSubType];
  } else {
    return (
      documentCategories[baseType] || {
        title: "",
        description: "",
        example: "",
      }
    );
  }
};
